import toast from 'react-hot-toast'
import { useHotkeys } from 'react-hotkeys-hook'
import { useOutletContext } from 'react-router'
import { useAllocateImages } from '@/features/photoset/queries/use-allocate-images'
import { useDeallocateImages } from '@/features/photoset/queries/use-deallocate-images'
import { usePhotosetStore } from '@/features/photoset/store/store.ts'
import type { PhotosetURLParams } from '@/features/photoset/types.ts'
import {
	generateToastAllocationId,
	hotKeysEventToSpace,
	spaceNameToShortcut
} from '@/features/photoset/utils.ts'
import { errorHandler, objectValues } from '@/helpers/utils'

export const useAllocationShortcuts = (options?: { disabled?: boolean }) => {
	const selectedImages = usePhotosetStore((store) => store.selectedImages)
	const undoLatestAction = usePhotosetStore((store) => store.undoLatestAction)
	const allocationHistory = usePhotosetStore(
		(store) => store.allocationHistory
	)
	const photosetSpacesDict = usePhotosetStore(
		(store) => store.photosetSpacesDict
	)
	const deallocateImages = usePhotosetStore((store) => store.deallocateImages)
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()
	const deallocateImagesMutation = useDeallocateImages()

	const allocateImagesMutation = useAllocateImages({
		options: {
			onError: (error) => errorHandler(error)
		},
		onUndo: async (imagesIds, photosetSpaceId) => {
			try {
				await deallocateImagesMutation.mutateAsync({
					urlParams: { photosetId: urlParams.id, photosetSpaceId },
					payload: imagesIds
				})

				const toastId = generateToastAllocationId(
					'allocation',
					photosetSpaceId,
					imagesIds
				)
				deallocateImages({
					imagesIds,
					photosetSpaceId,
					keepSelected: true,
					trackInHistory: false
				})
				toast.dismiss(toastId)
			} catch (error) {
				errorHandler(error)
			}
		}
	})

	useHotkeys(
		objectValues(spaceNameToShortcut),
		(_, hotkeysEvent) =>
			allocateImagesMutation.mutate({
				urlParams: {
					photosetId: urlParams.id,
					photosetSpaceId: hotKeysEventToSpace(
						hotkeysEvent,
						objectValues(photosetSpacesDict)
					)
				},
				payload: selectedImages
			}),
		{
			preventDefault: true,
			enableOnFormTags: true,
			enabled:
				!options?.disabled &&
				selectedImages.length >= 1 &&
				!allocateImagesMutation.isPending &&
				!deallocateImagesMutation.isPending
		},
		[
			allocateImagesMutation.isPending,
			deallocateImagesMutation.isPending,
			selectedImages,
			objectValues(photosetSpacesDict),
			options
		]
	)
	useHotkeys(
		'mod+Z',
		async () => {
			try {
				await deallocateImagesMutation.mutateAsync({
					urlParams: {
						photosetId: urlParams.id,
						photosetSpaceId: allocationHistory[0].photosetSpaceId
					},
					payload: allocationHistory[0].imagesIds
				})

				const toastId = generateToastAllocationId(
					allocationHistory[0].type,
					allocationHistory[0].photosetSpaceId,
					allocationHistory[0].imagesIds
				)

				undoLatestAction(true)
				toast.dismiss(toastId)
			} catch (error) {
				errorHandler(error)
			}
		},
		{
			preventDefault: true,
			enableOnFormTags: true,
			enabled:
				!options?.disabled &&
				allocationHistory.length >= 1 &&
				!allocateImagesMutation.isPending &&
				!deallocateImagesMutation.isPending
		},
		[
			allocationHistory,
			allocateImagesMutation.isPending,
			deallocateImagesMutation.isPending,
			options
		]
	)
}
